import { Button, Flex, Stack, Title } from "@mantine/core";
import { IMinUserDepricated } from "@mctypes/global.types";
import { IIssueApi } from "@schemas/issueSchema";
import { IOtherAttributeConfigApi } from "@schemas/otherAttributesSchema";
import { skipToken } from "@reduxjs/toolkit/query";
import { IconPlus } from "@tabler/icons-react";
import { CellEditRequestEvent, ColDef, RowDragEndEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useCallback, useMemo } from "react";
import {
  useGetProjectByProjectIdConnectedTasksQuery,
  usePutProjectByProjectIdIssueConnectedTasksUpdateOrderMutation,
  usePutProjectByProjectIdIssueUpdateSimpleMutation,
} from "../../../../../../openapi";
import { AG_GRID_LOCALE_NOR } from "../../../../../agGridLocale";
import { FailedAlert } from "../../../../../components";
import { setShowFailedMessageThunk } from "../../../../../global/globalSlice";
import { selectCurrentProjectId } from "../../../../../global/globalSliceCommon";
import { useAppDispatch, useAppSelector } from "../../../../../hooks";
import { selectProjectPns } from "../../../../projectSelectors";
import { parseNorwegianDate } from "../../../admin/selectors";
import {
  selectIssueBoardBuckets,
  selectIssueBoardScopedMembers,
  selectUnlockedConnectedTaskCellKeys,
} from "../../issueSelectors";
import {
  lockConnectedTaskCellInRedux,
  thunkOpenIssueModalById,
  unlockConnectedTaskCellInRedux,
} from "../../issueSlice";
import { ISSUE_DATAFILEDS_ENUM } from "../../issueConstants";
import { getIssueActionNameOption, issueBoardHasClientAccess } from "../../issueUtils";
import { useCreateIssueMutation, useDoUpdateIssueMutation } from "../../issueRTKLegacy";
import { selectIssueBoardId } from "../../redux/selectors/issueCommonSelectors";
import { selectHttpIssueBoard } from "../../redux/selectors/issueRtkSelectors";
import { getConnectedTaskColumns } from "./columns/connectedTaskColumns";
import { getIssueBaseColumns } from "./columns/issueBaseColumns";
import {
  createDateColumnEditor as createDateColumnEditorFactory,
  createIssueColumnBaseDefGetter,
  createUserColumnEditor as createUserColumnEditorFactory,
  HideAndSort,
} from "./columns/issueColumnHelpers";
import { IssueTableColumnContext } from "./columns/issueTableColumnContext";
import { defaultIssueTableColDef } from "./issueTableGridConfig";
import { getLogValueFromCellValue } from "./issueTableFormatters";

const EMPTY_OTHER_ATTRIBUTES_CONFIG: IOtherAttributeConfigApi = {};
const showConnectedTaskColumn: HideAndSort = () => ({ hide: false, sort: null, type: "default" });
const getConnectedTaskCellKey = (issueId: string, field: string) => `${issueId}:${field}`;

const ConnectedTasksIssueboardTable: React.FC<{ issueId: string }> = ({ issueId }) => {
  const dispatch = useAppDispatch();
  const projectId = useAppSelector(selectCurrentProjectId);
  const issueBoardId = useAppSelector(selectIssueBoardId);
  const issueBoard = useAppSelector(selectHttpIssueBoard);
  const boardBuckets = useAppSelector(selectIssueBoardBuckets);
  const members = useAppSelector(selectIssueBoardScopedMembers);
  const pnsItems = useAppSelector(selectProjectPns);
  const unlockedConnectedTaskCellKeys = useAppSelector(selectUnlockedConnectedTaskCellKeys);
  const [addNewIssue] = useCreateIssueMutation();
  const [updateIssue] = useDoUpdateIssueMutation();
  const [updateIssueSimple] = usePutProjectByProjectIdIssueUpdateSimpleMutation();
  const [reorderConnectedTasks] = usePutProjectByProjectIdIssueConnectedTasksUpdateOrderMutation();
  const { data: tasks = [], isError } = useGetProjectByProjectIdConnectedTasksQuery(
    projectId == null ? skipToken : { issueId, projectId },
  );

  const columnContext = useMemo<IssueTableColumnContext | null>(() => {
    if (projectId == null || issueBoardId == null || issueBoard == null) {
      return null;
    }

    const getIssueColumnBaseDef = createIssueColumnBaseDefGetter(showConnectedTaskColumn);

    return {
      boardBuckets,
      boardHasClientAccess: issueBoardHasClientAccess(issueBoard),
      createDateColumnEditor: createDateColumnEditorFactory(),
      createUserColumnEditor: createUserColumnEditorFactory(members),
      getIssueColumnBaseDef,
      handleUpdateIssueField: async (taskId, attr, value, oldValue = "") => {
        await updateIssue({
          projectId,
          issueBoardId,
          issueId: taskId,
          attr,
          value,
          oldValue,
        });
      },
      hideAndSort: showConnectedTaskColumn,
      isOtherAttributeCellUnlocked: (taskId, attr) =>
        unlockedConnectedTaskCellKeys.includes(getConnectedTaskCellKey(taskId, attr)),
      issueBoard,
      issueBoardId,
      lockOtherAttributeCell: (taskId, attr) =>
        dispatch(lockConnectedTaskCellInRedux(getConnectedTaskCellKey(taskId, attr))),
      members,
      pnsItems,
      projectId,
      setSelectedIssue: (issue) => dispatch(thunkOpenIssueModalById(issue._id)),
      types: issueBoard.types?.map((type) => type.name) ?? [],
      unlockOtherAttributeCell: (taskId, attr) =>
        dispatch(unlockConnectedTaskCellInRedux(getConnectedTaskCellKey(taskId, attr))),
      updateItemMembers: (taskId, itemMembers: IMinUserDepricated[]) => {
        updateIssue({
          projectId,
          issueBoardId,
          issueId: taskId,
          attr: ISSUE_DATAFILEDS_ENUM.ITEMMEMBERS,
          value: itemMembers,
          oldValue: "",
        });
      },
    };
  }, [
    boardBuckets,
    dispatch,
    issueBoard,
    issueBoardId,
    members,
    pnsItems,
    projectId,
    unlockedConnectedTaskCellKeys,
    updateIssue,
  ]);

  const otherAttributesTasksConfig = issueBoard?.otherAttributesTasksConfig ?? EMPTY_OTHER_ATTRIBUTES_CONFIG;
  const columnDefs = useMemo<ColDef<IIssueApi>[]>(() => {
    if (columnContext == null) {
      return [];
    }

    return getConnectedTaskColumns({
      baseColumns: getIssueBaseColumns(columnContext) as ColDef<IIssueApi>[],
      columnContext,
      otherAttributesTasksConfig,
    });
  }, [columnContext, otherAttributesTasksConfig]);

  const onCellEditRequest = useCallback((event: CellEditRequestEvent<IIssueApi>) => {
    if (projectId == null || issueBoardId == null) {
      return;
    }

    const issue = event.data;
    const field = event.colDef.field;

    if (issue == null || field == null) {
      return;
    }

    let updateValue = event.newValue;
    if (field.startsWith("dates")) {
      if (event.newValue == null) {
        updateValue = "";
      } else {
        const newDate = parseNorwegianDate(event.newValue);
        const oldDate = parseNorwegianDate(event.oldValue);

        if (newDate != null && oldDate != null && newDate.getTime() === oldDate.getTime()) {
          return;
        }

        updateValue = newDate;
      }
    }

    if (
      field === ISSUE_DATAFILEDS_ENUM.TITLE ||
      field === ISSUE_DATAFILEDS_ENUM.DESCRIPTION ||
      field === ISSUE_DATAFILEDS_ENUM.CONCLUSION ||
      field === ISSUE_DATAFILEDS_ENUM.REFERENCEURL
    ) {
      updateIssueSimple({
        projectId,
        body: {
          _id: issue._id,
          property: {
            [field]: updateValue,
          },
        },
      });
      return;
    }

    updateIssue({
      projectId,
      issueBoardId,
      issueId: issue._id,
      attr: field,
      value: updateValue,
      oldValue: getLogValueFromCellValue(event.oldValue),
    });
  }, [issueBoardId, projectId, updateIssue, updateIssueSimple]);

  const onRowDragEnd = useCallback(async (event: RowDragEndEvent<IIssueApi>) => {
    if (projectId == null) {
      return;
    }

    const newOrder = event.api
      .getRenderedNodes()
      .map((node) => node.data?._id)
      .filter((taskId): taskId is string => taskId != null);

    try {
      await reorderConnectedTasks({
        projectId,
        body: {
          issueId,
          newOrder,
        },
      }).unwrap();
    } catch (error) {
      console.error(error);
      dispatch(setShowFailedMessageThunk("Feil ved endring av rekkefolge pa tilkoblede saker"));
    }
  }, [dispatch, issueId, projectId, reorderConnectedTasks]);

  const onAddNewIssue = useCallback(() => {
    if (projectId == null || issueBoardId == null) {
      return;
    }

    addNewIssue({
      projectId,
      issueBoardId,
      issueTitle: "",
      taskOrIssue: "TASK",
      issueIdIfItIsATask: issueId,
    });
  }, [addNewIssue, issueBoardId, issueId, projectId]);

  if (projectId == null || issueBoardId == null || issueBoard == null) {
    return null;
  }

  const taskNamePlural = getIssueActionNameOption(issueBoard.taskNameSetting, "plural");

  return (
    <Stack gap="xs">
      {isError === true && <FailedAlert />}
      <Flex align="center" gap="xs">
        <Title order={6} fw={400}>
          {taskNamePlural}
        </Title>
        <Button
          size="compact-xs"
          variant="outline"
          onClick={onAddNewIssue}
        >
          <IconPlus size={14} />
        </Button>
      </Flex>
      {tasks.length > 0 && (
        <AgGridReact
          popupParent={document.body}
          rowHeight={45}
          defaultColDef={defaultIssueTableColDef}
          domLayout="autoHeight"
          columnDefs={columnDefs}
          getRowId={(params) => params.data._id}
          rowData={tasks}
          stopEditingWhenCellsLoseFocus={true}
          animateRows={false}
          readOnlyEdit={true}
          onCellEditRequest={onCellEditRequest}
          suppressContextMenu={true}
          suppressCutToClipboard={true}
          maintainColumnOrder={true}
          localeText={AG_GRID_LOCALE_NOR}
          rowDragManaged={true}
          onRowDragEnd={onRowDragEnd}
        />
      )}
    </Stack>
  );
};

export default ConnectedTasksIssueboardTable;
